<template>
  <div class="container">
    <h2>{{ t('myRole.title') }}</h2>

    <div v-if="!myRole" class="no-role">
      <p>{{ t('myRole.noRoleSelected') }}</p>
      <button class="btn btn-primary" @click="showRoleSelector = true">
        {{ t('myRole.selectRole') }}
      </button>
    </div>

    <div v-else class="my-role-content">
      <div class="role-info card">
        <div class="role-header">
          <h3>{{ t(myRole.nameKey) }}</h3>
          <span :class="['badge', `badge-${myRole.type}`]">
            {{ t(`roleTypes.${myRole.type}`) }}
          </span>
        </div>

        <div class="ability-section">
          <h4>{{ t('myRole.ability') }}</h4>
          <p>{{ t(myRole.abilityKey) }}</p>
        </div>

        <div class="rules-section">
          <h4>{{ t('myRole.relevantRules') }}</h4>
          <ul>
            <li v-for="(ruleKey, index) in myRole.rulesKeys" :key="index">
              {{ t(ruleKey) }}
            </li>
          </ul>
        </div>

        <button class="btn btn-secondary" @click="showRoleSelector = true">
          {{ t('common.edit') }}
        </button>
      </div>

      <div class="notes-section card">
        <h4>{{ t('myRole.myNotes') }}</h4>
        <textarea
          v-model="notes"
          :placeholder="t('myRole.writeNote')"
          class="notes-textarea"
          @blur="saveNotes"
        />
      </div>
    </div>

    <RoleSelectorModal
      v-if="showRoleSelector"
      :current-role-id="myRoleId"
      @select="selectMyRole"
      @close="showRoleSelector = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Note } from '@botc/shared';
import { useRoles } from '../composables/useRoles';
import { useStorage } from '../composables/useStorage';
import RoleSelectorModal from '../components/RoleSelectorModal.vue';

const { t } = useI18n();
const { roles } = useRoles();
const { storedData, saveStoredData } = useStorage();

const showRoleSelector = ref(false);
const notes = ref('');

const myRoleId = computed(() => storedData.value.gameState?.myRoleId);

const myRole = computed(() => {
  if (!myRoleId.value) return null;
  return roles.value.find(r => r.id === myRoleId.value) || null;
});

// Notes aus Storage laden
watch(myRoleId, (newRoleId) => {
  if (newRoleId) {
    const roleNotes = storedData.value.gameState?.notes.find((n: Note) => !n.playerId);
    notes.value = roleNotes?.content || '';
  } else {
    notes.value = '';
  }
}, { immediate: true });

function selectMyRole(roleId: string): void {
  if (!storedData.value.gameState) {
    storedData.value.gameState = {
      currentDay: 1,
      isNight: false,
      players: [],
      notes: [],
    };
  }

  storedData.value.gameState.myRoleId = roleId;
  saveStoredData();
  showRoleSelector.value = false;
}

function saveNotes(): void {
  if (!storedData.value.gameState || !myRoleId.value) return;

  const noteIndex = storedData.value.gameState.notes.findIndex((n: Note) => !n.playerId);

  if (noteIndex >= 0) {
    storedData.value.gameState.notes[noteIndex].content = notes.value;
    storedData.value.gameState.notes[noteIndex].timestamp = Date.now();
  } else {
    storedData.value.gameState.notes.push({
      id: `note-${Date.now()}`,
      content: notes.value,
      timestamp: Date.now(),
    });
  }

  saveStoredData();
}
</script>

<style scoped>
.no-role {
  text-align: center;
  padding: var(--spacing-xxl);
}

.no-role p {
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-lg);
}

.my-role-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.role-info {
  background: linear-gradient(135deg, var(--color-bg-secondary), var(--color-bg-tertiary));
}

.role-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-lg);
}

.role-header h3 {
  margin: 0;
  font-size: 1.5rem;
}

.ability-section,
.rules-section {
  margin-bottom: var(--spacing-lg);
}

.ability-section h4,
.rules-section h4 {
  color: var(--color-accent-gold);
  margin-bottom: var(--spacing-sm);
}

.rules-section ul {
  list-style: none;
  padding-left: 0;
}

.rules-section li {
  padding: var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
  background-color: var(--color-bg-primary);
  border-radius: var(--radius-sm);
  border-left: 3px solid var(--color-accent-gold);
}

.notes-textarea {
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
}

.notes-section h4 {
  color: var(--color-accent-gold);
  margin-bottom: var(--spacing-sm);
}
</style>
